import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef, useCallback } from 'react';
import './MapTabs.css';
import MAP from './MAP';
import AnalysisView from './AnalysisView';
import VideoTab from './VideoTab';
import { useEventContext } from '../TIMEBAR/EventContext';
import { useSharedState } from './hooks/useSharedState';
import tabSyncCoordinator from './coordinators/TabSyncCoordinator';
import videoSyncController from './controllers/VideoSyncController';

const MapTabs = forwardRef(({ csvFiles, onTimeUpdate, onTrackObject }, ref) => {
  const [activeTab, setActiveTab] = useState(0);
  const isComponentMounted = useRef(true);
  const tabHeaderRef = useRef(null);
  const previousActiveTab = useRef(0);

  // 使用新的共享状态hook
  const sharedState = useSharedState();
  
  // 从context获取更新方法
  const { updateVideoState } = useEventContext();

  // Component refs
  const mapRef = useRef(null);
  const analysisViewRef = useRef(null);
  const videoTabRef = useRef(null);

  // Track component mount state
  useEffect(() => {
    isComponentMounted.current = true;
    
    // 在组件挂载时通知TabSyncCoordinator当前活动tab
    tabSyncCoordinator.setActiveTab(activeTab);
    
    // Create the sticky effect for tab headers
    const handleScroll = () => {
      if (tabHeaderRef.current) {
        const header = tabHeaderRef.current;
        const sticky = header.offsetTop;
        
        if (window.pageYOffset > sticky) {
          header.classList.add('sticky-tabs');
        } else {
          header.classList.remove('sticky-tabs');
        }
      }
    };
    
    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    return () => { 
      isComponentMounted.current = false;
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeTab]);

  // Get the active component ref
  const getActiveComponentRef = useCallback(() => {
    if (activeTab === 0) return mapRef.current;
    if (activeTab === 1) return analysisViewRef.current;
    return videoTabRef.current;
  }, [activeTab]);

  // Disable trajectory highlight
  const disableTrajectoryHighlight = useCallback((componentRef) => {
    if (!componentRef) return;
    
    if (componentRef.disableHighlight) {
      console.log('MapTabs: Disabling trajectory highlight');
      componentRef.disableHighlight();
    }
  }, []);

  // 处理内部时间更新
  const handleInternalTimeUpdate = useCallback((time) => {
    if (!isComponentMounted.current || sharedState.processingUpdate) return;
    
    // 更新context时间
    updateVideoState({ time }, 'mapTabs');
    
    // 同步到其他tab
    tabSyncCoordinator.syncVideoState(sharedState.videoUrl, time, activeTab);
    
    // 调用传统回调
    if (onTimeUpdate) {
      onTimeUpdate(time);
    }
  }, [onTimeUpdate, sharedState.processingUpdate, updateVideoState, activeTab, sharedState.videoUrl]);

  // Reset child components' timeframes
  const resetChildComponentTimeframes = useCallback((newTimeframe) => {
    if (!isComponentMounted.current) return;
    
    // Get active component
    const activeComponent = getActiveComponentRef();
    
    // Reset timeframe if supported
    if (activeComponent && activeComponent.resetTimeframe) {
      console.log(`MapTabs: Resetting timeframe to ${newTimeframe} in active component`);
      activeComponent.resetTimeframe(newTimeframe);
    }
  }, [getActiveComponentRef]);

  // Handle track object
  const handleTrackObject = useCallback((trackData) => {
    if (onTrackObject) {
      onTrackObject(trackData);
    }
  }, [onTrackObject]);

  // 添加一个新的useEffect来处理videoUrl的更新
  useEffect(() => {
    if (!isComponentMounted.current || !sharedState.videoUrl) return;
    
    const activeComponent = getActiveComponentRef();
    if (activeComponent && activeComponent.handleVideoSelect) {
      activeComponent.handleVideoSelect(
        sharedState.videoUrl,
        sharedState.currentTime,
        sharedState.isPlaying
      );
    }
  }, [sharedState.videoUrl, getActiveComponentRef]);

  // First, add a state to track if transition is in progress
  const [tabTransitionInProgress, setTabTransitionInProgress] = useState(false);
  const tabMountedRef = useRef(false);

  // Modify handleTabChange function
  const handleTabChange = useCallback((tabIndex) => {
    if (tabIndex === activeTab) return;
    
    console.log(`MapTabs: Switching from tab ${activeTab} to tab ${tabIndex}`);
    
    // Save current state snapshot with explicit robotId and videoUrl
    const stateSnapshot = {
      time: sharedState.currentTime,
      videoUrl: sharedState.videoUrl,
      robotId: sharedState.activeRobotId,
      eventId: sharedState.activeEventId,
      isPlaying: sharedState.isPlaying,
      timeframe: sharedState.timeframe
    };
    
    console.log('MapTabs: Current state snapshot:', stateSnapshot);
    
    // Temporarily disable updates to prevent sync loops
    tabSyncCoordinator.ignoreAllUpdates();
    
    // Disable current tab's highlight
    const activeComponent = getActiveComponentRef();
    if (activeComponent) {
      disableTrajectoryHighlight(activeComponent);
    }
    
    // Save previous tab index
    previousActiveTab.current = activeTab;
    
    // Mark transition as in progress
    setTabTransitionInProgress(true);
    tabMountedRef.current = false;
    
    // Update active tab - this will trigger component mount
    setActiveTab(tabIndex);
    
    // Notify coordinator tab is transitioning
    tabSyncCoordinator.startTabTransition(tabIndex, stateSnapshot);
  }, [
    activeTab,
    sharedState.currentTime,
    sharedState.videoUrl,
    sharedState.activeRobotId,
    sharedState.activeEventId,
    sharedState.isPlaying,
    sharedState.timeframe,
    disableTrajectoryHighlight,
    getActiveComponentRef
  ]);

  // Add a useEffect to handle the second phase of transition
  // This will run after the new component mounts
  useEffect(() => {
    if (!tabTransitionInProgress) return;
    
    // Give the component time to mount fully
    const timer = setTimeout(() => {
      if (!isComponentMounted.current) return;
      
      const newActiveComponent = getActiveComponentRef();
      if (!newActiveComponent) {
        console.warn('MapTabs: New active component still null after delay, aborting state transfer');
        tabSyncCoordinator.resumeUpdates();
        setTabTransitionInProgress(false);
        return;
      }
      
      // Get the snapshot from tabSyncCoordinator
      const stateSnapshot = tabSyncCoordinator.stateSnapshot;
      console.log('MapTabs: Applying state snapshot after component mount:', stateSnapshot);
      
      if (!stateSnapshot) {
        console.warn('MapTabs: No state snapshot available for transition');
        tabSyncCoordinator.resumeUpdates();
        setTabTransitionInProgress(false);
        return;
      }
      
      // 验证状态快照的完整性
      if (tabSyncCoordinator.verifyStateSnapshot && !tabSyncCoordinator.verifyStateSnapshot()) {
        console.warn('MapTabs: 状态快照验证失败');
      }
      
      // First update global state to ensure consistency
      updateVideoState({
        eventId: stateSnapshot.eventId,
        robotId: stateSnapshot.robotId,
        isPlaying: stateSnapshot.isPlaying,
        videoUrl: stateSnapshot.videoUrl,
        time: stateSnapshot.time
      }, 'mapTabs_tabChange');
      
      // Then handle video selection in the new component
      if (newActiveComponent.handleVideoSelect) {
        newActiveComponent.handleVideoSelect(
          stateSnapshot.videoUrl, 
          stateSnapshot.time,
          stateSnapshot.isPlaying,
          stateSnapshot.robotId // 显式传递 robotId
        ).then(() => {
          // Ensure time syncing
          if (newActiveComponent.handleSliderChange && stateSnapshot.time > 0) {
            newActiveComponent.handleSliderChange(stateSnapshot.time);
          }
          
          // Notify sync controller of time change
          videoSyncController.updateCurrentTime(stateSnapshot.time, 'mapTabs_tabChange');
          
          // Resume updates
          tabSyncCoordinator.completeTabTransition();
          tabSyncCoordinator.resumeUpdates();
          setTabTransitionInProgress(false);
        }).catch(error => {
          console.error('Error during video selection:', error);
          tabSyncCoordinator.resumeUpdates();
          setTabTransitionInProgress(false);
        });
      } else {
        // If component doesn't support video selection, at least sync time
        if (newActiveComponent.handleSliderChange && stateSnapshot.time > 0) {
          newActiveComponent.handleSliderChange(stateSnapshot.time);
        }
        
        // Resume updates
        tabSyncCoordinator.completeTabTransition();
        tabSyncCoordinator.resumeUpdates();
        setTabTransitionInProgress(false);
      }
    }, 200); // Increase this delay to ensure component is fully mounted
    
    return () => clearTimeout(timer);
  }, [
    tabTransitionInProgress,
    getActiveComponentRef,
    updateVideoState
  ]);

  // 优化handleSliderChange
  const handleSliderChange = useCallback((value) => {
    if (!isComponentMounted.current || sharedState.processingUpdate) return;
    
    // 更新context
    updateVideoState({ time: value }, 'mapTabs');
    
    // 更新当前active组件
    const activeComponent = getActiveComponentRef();
    if (activeComponent && activeComponent.handleSliderChange) {
      activeComponent.handleSliderChange(value);
    }
  }, [sharedState.processingUpdate, updateVideoState, getActiveComponentRef]);

  // 监听sharedState变化，确保tabs间保持同步
  useEffect(() => {
    if (!isComponentMounted.current || tabSyncCoordinator.transitioning) return;
    
    const activeComponent = getActiveComponentRef();
    if (activeComponent && activeComponent.handleSliderChange && sharedState.currentTime > 0) {
      activeComponent.handleSliderChange(sharedState.currentTime);
    }
  }, [sharedState.currentTime, getActiveComponentRef]);

  // 修改handleVideoSelect方法
  const handleVideoSelect = useCallback((videoUrl, startTime, keepPlaying = false, robotId = null) => {
    if (!isComponentMounted.current || sharedState.processingUpdate) return Promise.resolve();
    
    // 提取robotId，如果没有显式传递
    if (!robotId) {
      const robotIdMatch = videoUrl.match(/video(\d+)/);
      robotId = robotIdMatch ? parseInt(robotIdMatch[1]) : null;
    }
    
    // 确保 robotId 有效
    if (!robotId) {
      console.warn('MapTabs: 无效的 robotId，跳过视频切换');
      return Promise.resolve();
    }
    
    console.log(`MapTabs: 处理视频选择 - URL=${videoUrl}, robotId=${robotId}, time=${startTime}`);
    
    // 使用防抖处理状态更新
    return new Promise((resolve) => {
      const debouncedUpdate = setTimeout(() => {
        if (!isComponentMounted.current) return resolve();
        
        // 更新context
        updateVideoState({
          robotId,
          time: startTime,
          videoUrl: videoUrl,
          isPlaying: keepPlaying
        }, 'mapTabs');
        
        // 同步到其他tab，显式传递robotId
        tabSyncCoordinator.syncVideoState(videoUrl, startTime, activeTab);
        
        // 确保活动组件也更新
        const activeComponent = getActiveComponentRef();
        if (activeComponent && activeComponent.handleVideoSelect) {
          activeComponent.handleVideoSelect(videoUrl, startTime, keepPlaying, robotId)
            .then(resolve)
            .catch(error => {
              console.error('Error in handleVideoSelect:', error);
              resolve();
            });
        } else {
          resolve();
        }
      }, 50); // 使用50ms的延迟来减少不必要的更新
      
      return () => clearTimeout(debouncedUpdate);
    });
  }, [sharedState.processingUpdate, updateVideoState, getActiveComponentRef, activeTab]);

  // Expose methods to parent components
  useImperativeHandle(ref, () => ({
    handleSliderChange,
    
    handleVideoSelect: (videoUrl, startTime, keepPlaying = false, robotId = null) => {
      if (!isComponentMounted.current || sharedState.processingUpdate) return Promise.resolve();
      
      // 提取robotId，如果没有显式传递
      if (!robotId) {
        const robotIdMatch = videoUrl.match(/video(\d+)/);
        robotId = robotIdMatch ? parseInt(robotIdMatch[1]) : null;
      }
      
      console.log(`MapTabs: 处理视频选择(对外) - URL=${videoUrl}, robotId=${robotId}, time=${startTime}`);
      
      // 更新context
      updateVideoState({
        robotId,
        time: startTime,
        videoUrl: videoUrl,
        isPlaying: keepPlaying
      }, 'mapTabs');
      
      // 同步到其他tab
      tabSyncCoordinator.syncVideoState(videoUrl, startTime, activeTab);
      
      // 确保活动组件也更新 - 显式传递robotId
      const activeComponent = getActiveComponentRef();
      if (activeComponent && activeComponent.handleVideoSelect) {
        return activeComponent.handleVideoSelect(videoUrl, startTime, keepPlaying, robotId);
      }
      
      return Promise.resolve();
    },
    
    handleTimeBlockClick: (startTime, robotId, eventId) => {
      if (!isComponentMounted.current || sharedState.processingUpdate) return;
      
      console.log(`MapTabs: Time block clicked - Time: ${startTime}, Robot: ${robotId}, Event: ${eventId}`);
      
      // Build correct video URL
      const timePart = sharedState.timeframe === "11AM" ? "day" : "night";
      const newVideoUrl = `/${timePart}/videos/video${robotId}.mp4`;
      
      // Update context
      updateVideoState({
        eventId,
        robotId,
        time: startTime,
        videoUrl: newVideoUrl
      }, 'mapTabs');
      
      // 确保活动组件也更新
      const activeComponent = getActiveComponentRef();
      if (activeComponent && activeComponent.handleTimeBlockClick) {
        activeComponent.handleTimeBlockClick(startTime, robotId, eventId);
      }
    },
    
    updateIconsVisibility: (routeIndex, visible) => {
      if (!isComponentMounted.current) return;
      
      const activeComponent = getActiveComponentRef();
      if (activeComponent && activeComponent.updateIconsVisibility) {
        activeComponent.updateIconsVisibility(routeIndex, visible);
      }
    },
    
    getCurrentTime: () => sharedState.currentTime,
    
    resetTimeframe: (newTimeframe) => {
      if (!isComponentMounted.current) return;
      
      console.log(`MapTabs: Resetting timeframe to ${newTimeframe}`);
      
      // 先清除所有地图
      if (mapRef.current && mapRef.current.clearMap) {
        mapRef.current.clearMap();
      }
      if (analysisViewRef.current && analysisViewRef.current.clearMap) {
        analysisViewRef.current.clearMap();
      }
      if (videoTabRef.current && videoTabRef.current.clearMap) {
        videoTabRef.current.clearMap();
      }
      
      // 更新全局 timeframe
      updateVideoState({ timeframe: newTimeframe }, 'mapTabs');
      
      // 重建所有地图
      if (mapRef.current && mapRef.current.rebuildMap) {
        mapRef.current.rebuildMap();
      }
      if (analysisViewRef.current && analysisViewRef.current.rebuildMap) {
        analysisViewRef.current.rebuildMap();
      }
      if (videoTabRef.current && videoTabRef.current.rebuildMap) {
        videoTabRef.current.rebuildMap();
      }
      
      // 更新子组件
      resetChildComponentTimeframes(newTimeframe);
      
      // 通知 TabSyncCoordinator 更新 timeframe
      tabSyncCoordinator.updateTimeframe(newTimeframe);
    },

    clearMap: () => {
      if (mapRef.current && mapRef.current.clearMap) {
        mapRef.current.clearMap();
      }
      if (analysisViewRef.current && analysisViewRef.current.clearMap) {
        analysisViewRef.current.clearMap();
      }
      if (videoTabRef.current && videoTabRef.current.clearMap) {
        videoTabRef.current.clearMap();
      }
    },
    
    // 修改 resetTimeframe 方法，先调用 clearMap
    resetTimeframe: (newTimeframe) => {
      if (!isComponentMounted.current) return;
      
      console.log(`MapTabs: Resetting timeframe to ${newTimeframe}`);
      
      // 先清除所有地图
      if (mapRef.current && mapRef.current.clearMap) {
        mapRef.current.clearMap();
      }
      if (analysisViewRef.current && analysisViewRef.current.clearMap) {
        analysisViewRef.current.clearMap();
      }
      if (videoTabRef.current && videoTabRef.current.clearMap) {
        videoTabRef.current.clearMap();
      }
      
      // 更新全局 timeframe
      updateVideoState({ timeframe: newTimeframe }, 'mapTabs');
      
      // 重建所有地图
      if (mapRef.current && mapRef.current.rebuildMap) {
        mapRef.current.rebuildMap();
      }
      if (analysisViewRef.current && analysisViewRef.current.rebuildMap) {
        analysisViewRef.current.rebuildMap();
      }
      if (videoTabRef.current && videoTabRef.current.rebuildMap) {
        videoTabRef.current.rebuildMap();
      }
      
      // 更新子组件
      resetChildComponentTimeframes(newTimeframe);
    },
    
    // 公开当前活动Tab索引
    getActiveTabIndex: () => activeTab,
    
    // 设置活动Tab
    setActiveTab: handleTabChange,
    
    handleTrackObject
  }), [
    sharedState.currentTime, 
    getActiveComponentRef, 
    handleTrackObject, 
    sharedState.processingUpdate, 
    resetChildComponentTimeframes, 
    sharedState.timeframe, 
    updateVideoState,
    handleTabChange,
    activeTab,
    sharedState.videoUrl,
    handleSliderChange
  ]);

  // Ensure new tab applies current time when mounted
  useEffect(() => {
    if (!isComponentMounted.current || !sharedState.currentTime) return;
    
    // Short delay to ensure component has loaded
    const timer = setTimeout(() => {
      if (!isComponentMounted.current) return;
      
      const activeComponent = getActiveComponentRef();
      if (activeComponent && activeComponent.handleSliderChange) {
        console.log(`MapTabs: Initializing new tab with time ${sharedState.currentTime}`);
        activeComponent.handleSliderChange(sharedState.currentTime);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [activeTab, sharedState.currentTime, getActiveComponentRef]);

  return (
    <div className="map-tabs-container">
      <div className="map-tabs-header" ref={tabHeaderRef}>
        <label className={`map-tab ${activeTab === 0 ? 'active' : ''}`}>
          <input 
            type="radio" 
            name="map-tab" 
            checked={activeTab === 0}
            onChange={() => handleTabChange(0)}
            className="map-tab-radio"
          />
          <span className="radio-label">Map</span>
        </label>
        <label className={`map-tab ${activeTab === 1 ? 'active' : ''}`}>
          <input 
            type="radio" 
            name="map-tab" 
            checked={activeTab === 1}
            onChange={() => handleTabChange(1)}
            className="map-tab-radio"
          />
          <span className="radio-label">Combined</span>
        </label>
        <label className={`map-tab ${activeTab === 2 ? 'active' : ''}`}>
          <input 
            type="radio" 
            name="map-tab" 
            checked={activeTab === 2}
            onChange={() => handleTabChange(2)}
            className="map-tab-radio"
          />
          <span className="radio-label">Video</span>
        </label>
      </div>
      
      <div className="map-tabs-content">
        {activeTab === 0 && (
          <MAP 
            ref={mapRef} 
            csvFiles={csvFiles} 
            onTimeUpdate={handleInternalTimeUpdate}
            timeframe={sharedState.timeframe}
            onTrackObject={handleTrackObject}
            initialTime={sharedState.currentTime}
            activeRobotId={sharedState.activeRobotId} // Add this
          />
        )}
        {activeTab === 1 && (
          <AnalysisView 
            ref={analysisViewRef}
            csvFiles={csvFiles}
            onTimeUpdate={handleInternalTimeUpdate}
            timeframe={sharedState.timeframe}
            onTrackObject={handleTrackObject}
            initialTime={sharedState.currentTime}
            activeRobotId={sharedState.activeRobotId} // Add this
          />
        )}
        {activeTab === 2 && (
          <VideoTab
            ref={videoTabRef}
            csvFiles={csvFiles}
            onTimeUpdate={handleInternalTimeUpdate}
            timeframe={sharedState.timeframe}
            onTrackObject={handleTrackObject}
            initialTime={sharedState.currentTime}
            activeRobotId={sharedState.activeRobotId} // Add this
          />
        )}
      </div>
    </div>
  );
});

export default MapTabs;